// @flow

import type {Product} from "../models/Product";

export type PreviewVehicle = {
	key: string,
	title: string,
	count: ?number,
	isSeries: boolean,
	imageUrl: ?string,
};

export type PreviewCategory = {
	key: string,
	name: string,
	title: string,
	count: ?number,
	imageUrl: ?string,
};

export type SearchPreview = {
	vehicles: PreviewVehicle[],
	categories: PreviewCategory[],
	products: Product[],
	totalItems: number,
};

export const MAX_PREVIEW_VEHICLES = 3;
export const MAX_PREVIEW_CATEGORIES = 3;
export const MAX_PREVIEW_PRODUCTS = 6;

// a word shorter than this matches inside nearly every title, and the search itself starts there too
const MINIMUM_WORD_LENGTH = 2;

export const typedWords = (typed: string = ""): string[] =>
	typed.toLowerCase().split(/\s+/).filter(word => word.length >= MINIMUM_WORD_LENGTH);

const mentionsAnyOf = (words: string[]) => (title: string = "") => {
	const lowerTitle = title.toLowerCase();

	return words.some(word => lowerTitle.includes(word));
};

const byCountDescending = (a, b) => (b.count || 0) - (a.count || 0);

/**
 * The picture of a vehicle or category node: its own, or else the one of the nearest node above it.
 * Few sub categories and no vehicle model carry a picture of their own, while their main category
 * and their series mostly do - and that picture shows what they belong to, which says more than the
 * placeholder the preview falls back to when there is none up the tree either.
 */
const imageFinderFor = (trees: any[]) => {
	const byKey = new Map();
	const collect = nodes => (nodes || []).forEach(node => {
		if (!byKey.has(node.key)) byKey.set(node.key, node);
		collect(node.categories);
	});
	collect(trees);

	const imageOf = (node, depth = 0) => {
		if (!node || depth > 10) return null;
		if (node.imageUrl) return node.imageUrl;

		return imageOf(byKey.get(node.parentKey), depth + 1);
	};

	return node => imageOf(byKey.get(node.key) || node);
};

/**
 * Turns the answer of a product search for what has been typed into the groups of the search
 * preview.
 *
 * <p>The facets of a search answer describe its hits, not the typed text: "golf" finds products that
 * also fit a Passat, and the Passat is among the vehicles, and its hits lie among the floor mats. But
 * picking a vehicle or a category opens all of it, not the hits - so only those are offered whose
 * title carries a typed word, for they are what the customer asked for. "golf" offers no category,
 * "fußm" offers the floor mats.
 */
export const toSearchPreview = (response: any, typed: string): SearchPreview => {
	const words = typedWords(typed);
	const isMentioned = mentionsAnyOf(words);
	const filterStats = (response && response.filterStats) || {};
	const pagedProducts = (response && response.pagedProducts) || {};

	const series = (filterStats.vehicleSeries || []).map(vehicle => ({...vehicle, isSeries: true}));
	const models = (filterStats.vehicleModels || []).map(vehicle => ({...vehicle, isSeries: false}));
	const vehicleImageOf = imageFinderFor([...(filterStats.vehicleSeries || []), ...(filterStats.vehicleModels || [])]);
	const categoryImageOf = imageFinderFor(filterStats.categories || []);
	const seenVehicles = new Set();
	const vehicles = [...series, ...models]
		.filter(vehicle => vehicle.count > 0 && isMentioned(vehicle.title))
		.filter(vehicle => !seenVehicles.has(vehicle.key) && seenVehicles.add(vehicle.key))
		.sort(byCountDescending)
		.slice(0, MAX_PREVIEW_VEHICLES)
		.map(vehicle => ({
			key: vehicle.key,
			title: vehicle.title,
			count: vehicle.count,
			isSeries: vehicle.isSeries,
			imageUrl: vehicleImageOf(vehicle),
		}));

	// the main categories group too coarsely to be worth a click of their own; their sub categories
	// are what the category pages are made of
	const subCategories = (filterStats.categories || [])
		.flatMap(category => (category.categories && category.categories.length > 0) ? category.categories : [category])
		.filter(category => category.count > 0);
	const categories = subCategories
		.filter(category => isMentioned(category.title))
		.sort(byCountDescending)
		.slice(0, MAX_PREVIEW_CATEGORIES)
		.map(category => ({
			key: category.key,
			name: category.name,
			title: category.title,
			count: category.count,
			imageUrl: categoryImageOf(category),
		}));

	return {
		vehicles,
		categories,
		products: (pagedProducts.productItems || []).slice(0, MAX_PREVIEW_PRODUCTS),
		totalItems: pagedProducts.totalItems || 0,
	};
};

/**
 * The number of products each category holds in a search answer - read off an answer without a
 * phrase, it is what the category page counts.
 */
export const categoryCountsOf = (response: any): {[string]: number} => {
	const counts = {};

	((response && response.filterStats && response.filterStats.categories) || [])
		.flatMap(category => [category, ...(category.categories || [])])
		.forEach(category => {
			counts[category.key] = category.count;
		});

	return counts;
};

/**
 * Puts on each vehicle and category the number of products its page shows, in place of the number of
 * hits for the typed words the facets carried: picking an entry opens all of it, not the hits, and
 * the number has to say what opens. Where a number is not known it is left out rather than guessed.
 */
export const withTotals = (
	preview: SearchPreview,
	categoryCounts: {[string]: number},
	vehicleCounts: {[string]: number},
): SearchPreview => ({
	...preview,
	vehicles: preview.vehicles.map(vehicle => ({...vehicle, count: vehicleCounts[vehicle.key]})),
	categories: preview.categories.map(category => ({...category, count: categoryCounts[category.key]})),
});

export const isEmptyPreview =(preview: ?SearchPreview): boolean =>
	!preview || (preview.vehicles.length === 0 && preview.categories.length === 0 && preview.products.length === 0);

/**
 * Splits a title into the parts a typed word matches and the parts in between, so the match can be
 * set off - "Gummifußmatte" for "fußm" gives ["Gummi", "fußm", "atte"] with the middle one marked.
 */
export const splitByMatches = (title: string = "", typed: string = ""): {text: string, isMatch: boolean}[] => {
	const words = typedWords(typed)
		.map(word => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
		.sort((a, b) => b.length - a.length);

	if (words.length === 0) {
		return [{text: title, isMatch: false}];
	}

	const pattern = new RegExp(`(${words.join("|")})`, "gi");

	return title
		.split(pattern)
		.filter(part => part.length > 0)
		.map(part => ({text: part, isMatch: words.some(word => new RegExp(`^${word}$`, "i").test(part))}));
};
